import * as React from "react";
import { useAuth } from "../../../../lib/auth/hooks";
import * as authApi from "../../../../lib/api/auth";
import { ApiClientError } from "../../../../lib/api/client";
import { isEmailCodeExpired } from "../../../../lib/auth/user-setup-progress";
import {
  normalizeSetupSearchCode,
  type UserSetupSearch,
} from "../../../../lib/auth/user-setup-search";
import { getInviteToken } from "../../../../lib/auth/invite-pending";

export function useEmailVerification(options?: {
  active?: boolean;
  search?: UserSetupSearch;
  /** Send verification email when the flow becomes active (profile modal). */
  requestOnOpen?: boolean;
  onManualCodeVerify?: (code: string) => void;
  onClearVerification?: () => void;
}) {
  const { user, accessToken, refreshUser, refreshAccessToken } = useAuth();
  const search = options?.search ?? {};
  const active = options?.active ?? true;
  const onClearVerification = options?.onClearVerification;

  const [error, setError] = React.useState<string | null>(null);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [verifyStatus, setVerifyStatus] = React.useState<"idle" | "loading" | "success" | "error">("idle");
  const [verifyMessage, setVerifyMessage] = React.useState<string | null>(null);
  const activationAttempted = React.useRef<string | null>(null);
  const sentOnOpenRef = React.useRef(false);
  const [pendingCodeProfileSync, setPendingCodeProfileSync] = React.useState(false);

  const displayEmail = user?.email ?? search.email ?? "your@email.com";
  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const clearSessionState = React.useCallback(() => {
    setError(null);
    setVerifyStatus("idle");
    setVerifyMessage(null);
    setResendLoading(false);
    activationAttempted.current = null;
  }, []);

  React.useEffect(() => {
    if (active) return;
    clearSessionState();
    sentOnOpenRef.current = false;
  }, [active, clearSessionState]);

  const markAlreadyVerified = React.useCallback(() => {
    setVerifyStatus("idle");
    setVerifyMessage(null);
    setError(null);
    onClearVerification?.();
  }, [onClearVerification]);

  const runEmailActivation = React.useCallback(
    async (email: string, code: string) => {
      setVerifyStatus("loading");
      setVerifyMessage(null);
      setError(null);
      try {
        const inviteToken = getInviteToken();
        await authApi.verifyEmail(email, code, inviteToken ?? undefined);
        try {
          await refreshAccessToken();
        } catch {
          await refreshUser();
        }
        setVerifyStatus("success");
        setVerifyMessage("Your email has been verified.");
        onClearVerification?.();
      } catch (e) {
        setVerifyStatus("error");
        setVerifyMessage(
          e instanceof ApiClientError
            ? e.message
            : "Email verification failed. The code may be invalid or expired.",
        );
      }
    },
    [refreshAccessToken, refreshUser, onClearVerification],
  );

  React.useEffect(() => {
    if (!active) {
      setPendingCodeProfileSync(false);
      return;
    }
    const code = normalizeSetupSearchCode(search.code);
    if (!code || code.length !== 6) {
      setPendingCodeProfileSync(false);
      return;
    }
    let cancelled = false;
    setPendingCodeProfileSync(true);
    void refreshUser().finally(() => {
      if (!cancelled) setPendingCodeProfileSync(false);
    });
    return () => {
      cancelled = true;
    };
  }, [active, search.code, refreshUser]);

  React.useEffect(() => {
    if (!active || pendingCodeProfileSync) return;
    if (user?.emailVerified) {
      markAlreadyVerified();
      return;
    }
    const email = search.email ?? user?.email;
    const code = normalizeSetupSearchCode(search.code);
    if (!email || !code || code.length !== 6) return;
    const key = `${email}:${code}`;
    if (activationAttempted.current === key) return;
    activationAttempted.current = key;
    void runEmailActivation(email, code);
  }, [
    active,
    pendingCodeProfileSync,
    user?.emailVerified,
    search.email,
    search.code,
    user?.email,
    runEmailActivation,
    markAlreadyVerified,
  ]);

  const handleResendEmail = React.useCallback(async () => {
    if (!token) {
      setError("Session expired. Please sign in again.");
      return;
    }
    setError(null);
    setVerifyStatus("idle");
    setVerifyMessage(null);
    activationAttempted.current = null;
    onClearVerification?.();
    setResendLoading(true);
    try {
      await authApi.requestEmailVerification(token);
      await refreshUser();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : "Failed to resend verification email");
    } finally {
      setResendLoading(false);
    }
  }, [token, refreshUser, onClearVerification]);

  const requestOnOpen = options?.requestOnOpen ?? false;

  React.useEffect(() => {
    if (!active || !requestOnOpen || !token || user?.emailVerified) return;
    if (sentOnOpenRef.current) return;
    sentOnOpenRef.current = true;
    void handleResendEmail();
  }, [active, requestOnOpen, token, user?.emailVerified, handleResendEmail]);

  const handleManualCodeVerify = React.useCallback(
    (code: string) => {
      if (user?.emailVerified) {
        markAlreadyVerified();
        return;
      }
      if (options?.onManualCodeVerify) {
        options.onManualCodeVerify(code);
        return;
      }
      void runEmailActivation(displayEmail, code);
    },
    [displayEmail, runEmailActivation, options?.onManualCodeVerify, user?.emailVerified, markAlreadyVerified],
  );

  const clearEmailVerificationState = React.useCallback(() => {
    clearSessionState();
    onClearVerification?.();
  }, [clearSessionState, onClearVerification]);

  const sectionError =
    error ??
    (verifyStatus === "error" && !user?.emailVerified ? verifyMessage : null) ??
    (!user?.emailVerified && isEmailCodeExpired(user)
      ? "Your verification code has expired. Request a new email to get a fresh code."
      : null);

  return {
    user,
    search,
    displayEmail,
    verifyStatus,
    verifyMessage,
    resendLoading,
    sectionError,
    handleResendEmail,
    handleManualCodeVerify,
    clearEmailVerificationState,
  };
}
